import { localePath, type Locale } from "@/lib/i18n";

const STORAGE_KEY = "machuping:contact-support-draft:v1";
const STORAGE_VERSION = 1;
const MAX_AGE_MS = 30 * 60 * 1_000;

type ContactSupportDraftStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export type ContactSupportDraft = {
  email: string;
  message: string;
};

type StoredContactSupportDraft = ContactSupportDraft & {
  version: typeof STORAGE_VERSION;
  createdAt: number;
};

export function contactSupportPath(locale: Locale): string {
  return localePath(locale, "/contact");
}

export function storeContactSupportDraft(
  storage: ContactSupportDraftStorage,
  draft: ContactSupportDraft,
  now = Date.now(),
): boolean {
  const stored: StoredContactSupportDraft = {
    version: STORAGE_VERSION,
    createdAt: now,
    email: draft.email.trim().slice(0, 254),
    message: draft.message.trim().slice(0, 1_000),
  };
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(stored));
    return true;
  } catch {
    return false;
  }
}

export function takeContactSupportDraft(
  storage: ContactSupportDraftStorage,
  now = Date.now(),
): ContactSupportDraft | null {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    storage.removeItem(STORAGE_KEY);
    const parsed = JSON.parse(raw) as Partial<StoredContactSupportDraft> | null;
    if (
      !parsed
      || parsed.version !== STORAGE_VERSION
      || typeof parsed.createdAt !== "number"
      || parsed.createdAt > now
      || now - parsed.createdAt > MAX_AGE_MS
      || typeof parsed.email !== "string"
      || typeof parsed.message !== "string"
    ) {
      return null;
    }
    return {
      email: parsed.email.slice(0, 254),
      message: parsed.message.slice(0, 1_000),
    };
  } catch {
    return null;
  }
}
